import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import {
  AppState,
  CategoryFilterOption,
  ContractTypeOption,
  FilterState,
  LivingSpaceRange,
  PriceRange,
  PropertySpaceRange,
  RadiusFilterOption,
  Region,
  RoomCountRange,
  SubcategoryOptionsMap,
  UsageSpaceRange,
} from '@immosky/types';
import { select, Store } from '@ngrx/store';
import {
  availableRadiusOptions,
  removeRegionFilter,
  resetAllFilters,
  resetRegionFilter,
  selectFilterState,
  setContractTypeFilter,
  setLivingSpaceFilter,
  setMainCategoryFilter,
  setPriceFilter,
  setPropertySpaceFilter,
  setRoomCountFilter,
  setSubcategoryFilter,
  setUsageSpaceFilter,
} from '@immosky/object-search-filter';
import { Subscription } from 'rxjs';
import { setIsMobileFilterOpen } from '@immosky/object-search-layout';

@Component({
  selector: 'app-mobile-step-one',
  templateUrl: './mobile-step-one.component.html',
  styleUrls: ['./mobile-step-one.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileStepOneComponent implements OnDestroy {
  private subscriptions: Subscription[] = [];

  regions: Region[] = [];
  filterState!: FilterState;

  displaySubcategory = false;
  displayRoomCount = false;
  displayLivingSpace = false;
  displayUsageSpace = false;
  displayPropertySpace = false;

  radiusOptions: RadiusFilterOption[] = availableRadiusOptions;

  get isCategoryActive(): boolean {
    return this.filterState.category.main.isActive;
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>
  ) {
    const filterSubscription = this.store
      .pipe(select(selectFilterState))
      .subscribe((filterState: FilterState) => {
        this.filterState = filterState;
        this.regions = filterState.region.regions;

        this.displaySubcategory = filterState.category.sub.isDisplayed;
        this.displayRoomCount = filterState.roomCount.isDisplayed;
        this.displayLivingSpace = filterState.livingSpace.isDisplayed;
        this.displayUsageSpace = filterState.usageSpace.isDisplayed;
        this.displayPropertySpace = filterState.propertySpace.isDisplayed;

        this.cdRef.markForCheck();
      });

    this.subscriptions.push(filterSubscription);
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  close() {
    this.store.dispatch(
      setIsMobileFilterOpen({
        isOpen: false,
      })
    );
  }

  resetAll() {
    this.store.dispatch(resetAllFilters());
  }

  onContractTypeChange(filter: ContractTypeOption) {
    this.store.dispatch(
      setContractTypeFilter({
        filter,
      })
    );
  }

  onCategoryChange(filter: CategoryFilterOption) {
    this.store.dispatch(
      setMainCategoryFilter({
        filter,
      })
    );
  }

  onSubcategoryChange(filter: SubcategoryOptionsMap) {
    this.store.dispatch(
      setSubcategoryFilter({
        filter,
      })
    );
  }

  onPriceChange(filter: PriceRange) {
    this.store.dispatch(
      setPriceFilter({
        filter,
      })
    );
  }

  onRoomCountChange(filter: RoomCountRange) {
    this.store.dispatch(
      setRoomCountFilter({
        filter,
      })
    );
  }

  onLivingSpaceChange(filter: LivingSpaceRange) {
    this.store.dispatch(
      setLivingSpaceFilter({
        filter,
      })
    );
  }

  onUsageSpaceChange(filter: UsageSpaceRange) {
    this.store.dispatch(
      setUsageSpaceFilter({
        filter,
      })
    );
  }

  onPropertySpaceChange(filter: PropertySpaceRange) {
    this.store.dispatch(
      setPropertySpaceFilter({
        filter,
      })
    );
  }

  trackByRegion(index: number, region: Region) {
    return region.id;
  }

  removeRegionFilter(region: Region) {
    this.store.dispatch(
      removeRegionFilter({
        region,
      })
    );
  }

  resetAllRegions() {
    this.store.dispatch(resetRegionFilter());
  }
}
